
import React, { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import CreditBalanceDisplay from "@/components/credits/CreditBalanceDisplay";
import CreditPackageCard from "@/components/credits/CreditPackageCard";
import CustomCreditPackage from "@/components/credits/CustomCreditPackage";
import CreditInfoBanner from "@/components/credits/CreditInfoBanner";

export default function CreditsTab() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [transactionHistory, setTransactionHistory] = useState<any[]>([]);

  // Simulate loading transaction history
  useEffect(() => {
    const timer = setTimeout(() => {
      setTransactionHistory([
        {
          id: 'txn_1',
          type: 'purchase',
          amount: 10000,
          description: 'Standard Package Purchase',
          timestamp: '2025-05-01T10:30:00Z',
          stripe_session_id: 'cs_test_123456',
        },
        {
          id: 'txn_2',
          type: 'strategy_use',
          amount: -500,
          description: 'Momentum Strategy Execution',
          timestamp: '2025-05-03T14:15:00Z',
        },
        {
          id: 'txn_3',
          type: 'bonus',
          amount: 1000,
          description: 'Referral Bonus',
          timestamp: '2025-05-04T09:45:00Z',
        },
      ]);
      setIsLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTransactionTypeStyles = (type: string) => {
    switch(type) {
      case 'purchase':
        return {
          bgColor: 'bg-green-500/10',
          textColor: 'text-green-400',
          label: 'Purchase'
        };
      case 'strategy_use':
        return {
          bgColor: 'bg-orange-500/10',
          textColor: 'text-orange-400',
          label: 'Strategy'
        };
      case 'bonus':
        return {
          bgColor: 'bg-blue-500/10',
          textColor: 'text-blue-400',
          label: 'Bonus'
        };
      default:
        return {
          bgColor: 'bg-stewie-gray/10',
          textColor: 'text-stewie-gray',
          label: 'Transaction'
        };
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">Stewie Credits</h2>
        <p className="text-stewie-gray">
          Manage your credits for trading operations on the platform.
        </p>
      </div>

      <CreditBalanceDisplay />
      <CreditInfoBanner />

      <div className="settings-card">
        <h3 className="settings-header">Purchase Credits</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CreditPackageCard 
            title="Basic Package" 
            credits={1000} 
            price={1000} 
            description="Perfect for getting started"
            priceId="price_basic"
            featured={false}
          />
          
          <CreditPackageCard 
            title="Standard Package" 
            credits={10000} 
            price={10000} 
            description="Most popular choice"
            priceId="price_standard"
            featured={true}
          />
          
          <CreditPackageCard 
            title="Premium Package" 
            credits={25000} 
            price={25000} 
            description="For advanced traders"
            priceId="price_premium"
            featured={false}
          />
        </div>
        
        <div className="mt-6">
          <CustomCreditPackage />
        </div>
      </div>

      <div className="settings-card">
        <h3 className="settings-header">Transaction History</h3>
        
        {isLoading ? (
          <div className="py-8">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-stewie-gray/20 rounded-md"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-stewie-gray/20 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-stewie-gray/20 rounded w-1/2"></div>
                  </div>
                  <div className="h-6 w-24 bg-stewie-gray/20 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {transactionHistory.length === 0 ? (
              <div className="text-center py-8 text-stewie-gray">
                <p>No transaction history available.</p>
              </div>
            ) : (
              transactionHistory.map((transaction) => {
                const typeStyles = getTransactionTypeStyles(transaction.type);
                
                return (
                  <div 
                    key={transaction.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-stewie-dark/50 border border-stewie-gray/10 rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${typeStyles.bgColor}`}>
                        <span className={`text-lg font-bold ${typeStyles.textColor}`}>
                          {transaction.type === 'purchase' ? '+' : transaction.type === 'strategy_use' ? '-' : '*'}
                        </span>
                      </div>
                      
                      <div>
                        <p className="font-medium text-white">{transaction.description}</p>
                        <p className="text-xs text-stewie-gray">{formatDate(transaction.timestamp)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs ${typeStyles.bgColor} ${typeStyles.textColor}`}>
                        {typeStyles.label}
                      </span>
                      
                      <span className={`font-mono font-medium ${
                        transaction.amount > 0 ? 'text-green-400' : 'text-orange-400'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()} credits
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
